import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiArrowRight, FiCheck, FiPrinter, FiFileText, FiPackage, FiImage, FiBook } = FiIcons;

const PrintDesignService = () => {
  const services = [
    {
      icon: FiFileText,
      title: 'Business Stationery',
      description: 'Professional business cards, letterheads, and corporate stationery that make a lasting first impression.'
    },
    {
      icon: FiBook,
      title: 'Brochures & Catalogues',
      description: 'Eye-catching brochures and detailed catalogues that effectively showcase your products and services.'
    },
    {
      icon: FiPackage,
      title: 'Packaging Design',
      description: 'Creative packaging solutions that protect your products while enhancing brand recognition on the shelf.'
    },
    {
      icon: FiImage,
      title: 'Exhibition Materials',
      description: 'Stand-out banners, posters, and exhibition displays that attract attention at trade shows and events.'
    },
    {
      icon: FiPrinter,
      title: 'Annual Reports',
      description: 'Professional annual reports and corporate publications that communicate your company\'s achievements.'
    }
  ];

  const materials = [
    {
      name: 'Business Cards',
      description: 'Premium finishes and paper stocks'
    },
    {
      name: 'Brochures',
      description: 'Tri-fold, bi-fold, and custom formats'
    },
    {
      name: 'Flyers & Posters',
      description: 'High-impact promotional materials'
    },
    {
      name: 'Packaging',
      description: 'Product boxes, labels, and wrapping'
    },
    {
      name: 'Banners & Signage',
      description: 'Indoor and outdoor display solutions'
    },
    {
      name: 'Corporate Reports',
      description: 'Professional document design'
    }
  ];

  const packageFeatures = [
    'Custom design consultation',
    'Multiple design concepts',
    'Unlimited revisions',
    'High-resolution print files',
    'Print specifications guide',
    'Color management and proofing',
    'Preferred printer recommendations',
    'File format optimization',
    'Brand consistency guidelines',
    'Ongoing design support'
  ];

  const process = [
    {
      step: '01',
      title: 'Brief & Consultation',
      description: 'We discuss your requirements, target audience, and design preferences to understand your vision.',
      timeline: '1-2 days'
    },
    {
      step: '02',
      title: 'Concept Development',
      description: 'Create initial design concepts that align with your brand and communicate your message effectively.',
      timeline: '3-5 days'
    },
    {
      step: '03',
      title: 'Design Refinement',
      description: 'Refine chosen concepts based on your feedback, ensuring every detail meets your expectations.',
      timeline: '3-5 days'
    },
    {
      step: '04',
      title: 'Print Preparation',
      description: 'Prepare final files for print with proper color management and technical specifications.',
      timeline: '1-2 days'
    },
    {
      step: '05',
      title: 'Delivery & Support',
      description: 'Deliver final files and provide printing guidance to ensure optimal results.',
      timeline: '1 day'
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="font-poppins font-bold text-4xl md:text-5xl lg:text-6xl mb-6">
              Print <span className="text-purple-600">Design</span>
            </h1>
            <p className="font-inter text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-8">
              Professional print materials that make a lasting impression and effectively communicate your brand message in the physical world.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-purple-700 transition-all duration-300 transform hover:scale-105"
              >
                Start Your Project <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/portfolio"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-poppins font-semibold rounded-lg hover:bg-white hover:text-teal-600 transition-all duration-300"
              >
                View Our Work
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              What We Offer
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive print design services that bring your brand to life in tangible, impactful ways.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-lg card-hover border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-teal-600 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={service.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-poppins font-semibold text-xl text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="font-inter text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              Print Materials We Design
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto">
              From business cards to large format displays, we design print materials that make your brand stand out.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {materials.map((material, index) => (
              <motion.div
                key={material.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg p-6 shadow-md"
              >
                <h3 className="font-poppins font-semibold text-lg text-gray-900 mb-2">
                  {material.name}
                </h3>
                <p className="font-inter text-gray-600 text-sm">
                  {material.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              Our Design Process
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto">
              Our streamlined process ensures your print materials are designed efficiently and delivered with exceptional quality.
            </p>
          </motion.div>
          <div className="space-y-8">
            {process.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`flex flex-col lg:flex-row items-center gap-8 ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className="flex-1">
                  <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-teal-600 rounded-lg flex items-center justify-center mr-4">
                        <span className="font-poppins font-bold text-white">{step.step}</span>
                      </div>
                      <div>
                        <h3 className="font-poppins font-semibold text-xl text-gray-900">
                          {step.title}
                        </h3>
                        <p className="font-inter text-purple-600 font-medium text-sm">
                          {step.timeline}
                        </p>
                      </div>
                    </div>
                    <p className="font-inter text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Package & Pricing */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6">
                Complete Print Package
              </h2>
              <p className="font-inter text-lg text-gray-600 mb-8 leading-relaxed">
                Our comprehensive print design package includes everything you need to create professional materials that represent your brand beautifully.
              </p>
              <div className="space-y-3 mb-8">
                {packageFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="font-inter text-gray-600">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="bg-gradient-to-r from-purple-600 to-teal-600 rounded-xl p-6 text-white mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-inter text-sm opacity-90">Starting Investment</p>
                    <p className="font-poppins font-bold text-3xl">From R5,000</p>
                  </div>
                  <div className="text-right">
                    <p className="font-inter text-sm opacity-90">Timeline</p>
                    <p className="font-poppins font-semibold text-lg">1-2 weeks</p>
                  </div>
                </div>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-purple-700 transition-all duration-300 transform hover:scale-105"
              >
                Get Started Today <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-w-4 aspect-h-3">
                <img
                  src="https://images.unsplash.com/photo-1542744094-3a31f272c490?w=800&h=600&fit=crop"
                  alt="Print design materials"
                  className="w-full h-96 object-cover rounded-2xl shadow-xl"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-white rounded-2xl shadow-xl flex items-center justify-center">
                <div className="text-center">
                  <div className="font-poppins font-bold text-2xl text-purple-600">200+</div>
                  <div className="font-inter text-sm text-gray-600">Print Projects</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4">
              Ready to Print Your Success?
            </h2>
            <p className="font-inter text-lg text-gray-300 max-w-2xl mx-auto mb-8">
              Let's create print materials that make a lasting impression and effectively represent your brand in the physical world.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-purple-700 transition-all duration-300 transform hover:scale-105"
              >
                Start Your Print Project <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/portfolio"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-poppins font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-300"
              >
                See Our Portfolio
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrintDesignService;